import Head from 'next/head'
import type { FC } from 'react'
import type { DeepPartial } from 'ts-essentials'

import type { SeoMetadata } from '@/types/datocms-schema'

import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { getSiteBaseUrl } from '@/lib/utils/localisationUtils'

export interface SEOMetaTagsProps {
  metaData?: DeepPartial<SeoMetadata> | null
  // Only for pages whose canonical intentionally differs from their own URL
  // (e.g. experiment variants canonicalising to the default page). Everything
  // else should let the component derive it from the router.
  canonicalPath?: string
  type?: string
  alternateUrls?: ReadonlyArray<{ hreflang: string; href: string }>
}

// Canonical/og:url must identify the PAGE, never the visit: strip query and
// hash so per-visitor params (e.g. the attribution lead_id, utms — WEB-902)
// can't fragment the canonical into infinite unique URLs for search engines.
// Trailing slashes are stripped because Next.js serves the slashless URL
// (it 308s `/foo/` → `/foo`), and the canonical must equal the served URL.
export const toCanonicalPath = (path: string): string => {
  const pageOnly = path.split(/[?#]/)[0]
  return pageOnly.length > 1 ? pageOnly.replace(/\/+$/, '') : pageOnly
}

export const SEOMetaTags: FC<SEOMetaTagsProps> = ({ metaData, canonicalPath, type, alternateUrls }) => {
  // asPath, not pathname: on dynamic routes pathname is the route pattern
  // (`/[locale]/daily30`) while asPath is the real URL, including in SSG
  // output — indexable pages are fallback: false/blocking, so the built HTML
  // gets the concrete requested path.
  const { asPath } = useTrackedRouter()
  const canonicalUrl = `${getSiteBaseUrl()}${toCanonicalPath(canonicalPath ?? asPath)}`

  return (
    <Head>
      {metaData?.title && (
        <>
          <title>{metaData.title}</title>
          <meta property="og:title" content={metaData.title} key="title" />
          <meta name="twitter:title" content={metaData.title} key="twitter-title" />
        </>
      )}
      {metaData?.description && (
        <>
          <meta name="description" content={metaData.description} key="description" />
          <meta property="og:description" content={metaData.description} key="og-description" />
          <meta name="twitter:description" content={metaData.description} key="twitter-description" />
        </>
      )}
      {metaData?.image && (
        <>
          <meta property="og:image" content={metaData.image?.responsiveImage?.src} key="og-image" />
          <meta name="twitter:image" content={metaData.image?.responsiveImage?.src} key="twitter-image" />
        </>
      )}
      <link rel="canonical" href={canonicalUrl} key="canonical" />
      <meta property="og:url" content={canonicalUrl} key="og-url" />
      {alternateUrls?.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}
      <meta property="og:type" content={type || 'website'} key="type" />
      {metaData?.twitterCard && (
        <>
          <meta name="twitter:site" content="@Join_ZOE" />
          <meta name="twitter:card" content={metaData.twitterCard} />
        </>
      )}
      {metaData?.noIndex && <meta name="robots" content="noindex,nofollow" key="robots" />}
    </Head>
  )
}
